import { useCallback, useEffect, useMemo, useState } from 'react';
import { TreeNode, TreeIndexMap, ContextMenuState } from '../types';
import { buildTreeIndex, renameNode, removeNode, moveNode, addChild, findNodeById, findParentId } from '../utils';

type UseTreeManagerParams = {
  initialSelectedNodeId?: string | null;
  onDeleteCleanup?: (removedIds: string[]) => void;
  onMoveCleanup?: (pathChanges: Record<string, string>) => void;
};

export function useTreeManager({
  initialSelectedNodeId = null,
  onDeleteCleanup,
  onMoveCleanup,
}: UseTreeManagerParams = {}) {
  const [tree, setTree] = useState<TreeNode[]>([]);
  const [treeIndex, setTreeIndex] = useState<TreeIndexMap>(() => buildTreeIndex([]));
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [treeFilter, setTreeFilter] = useState('');
  const [debouncedTreeFilter, setDebouncedTreeFilter] = useState('');
  const [fileTypeFilter, setFileTypeFilter] = useState<string[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(initialSelectedNodeId);
  const [multiSelectedIds, setMultiSelectedIds] = useState<Set<string>>(new Set());
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);
  const [clipboard, setClipboard] = useState<{ ids: string[]; operation: 'copy' | 'cut' } | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    x: 0,
    y: 0,
    node: null,
    open: false,
  });

  // Debounce tree filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTreeFilter(treeFilter);
    }, 200);
    return () => clearTimeout(timer);
  }, [treeFilter]);

  // Extract file extension from name
  const getFileExtension = useCallback((name: string): string => {
    const lastDot = name.lastIndexOf('.');
    if (lastDot <= 0) return '';
    return name.slice(lastDot + 1).toLowerCase();
  }, []);

  // Get all unique file extensions in the tree
  const availableFileTypes = useMemo(() => {
    const extensions = new Set<string>();
    const collectExtensions = (nodes: TreeNode[]) => {
      nodes.forEach(node => {
        if (node.type === 'file') {
          const ext = getFileExtension(node.name);
          if (ext) extensions.add(ext);
        }
        if (node.children) collectExtensions(node.children);
      });
    };
    collectExtensions(tree);
    return Array.from(extensions).sort();
  }, [tree, getFileExtension]);

  const filteredTree = useMemo(() => {
    const hasTextFilter = debouncedTreeFilter.trim() !== '';
    const hasTypeFilter = fileTypeFilter.length > 0;

    if (!hasTextFilter && !hasTypeFilter) return tree;

    const q = debouncedTreeFilter.trim().toLowerCase();

    const matchesTextFilter = (node: TreeNode): boolean =>
      !hasTextFilter || node.name.toLowerCase().includes(q) || node.path.toLowerCase().includes(q);

    const matchesTypeFilter = (node: TreeNode): boolean => {
      if (!hasTypeFilter) return true;
      if (node.type === 'folder') return true; // Folders pass type filter
      const ext = getFileExtension(node.name);
      return fileTypeFilter.includes(ext);
    };

    const filterNodes = (nodes: TreeNode[]): TreeNode[] => {
      return nodes
        .map((node) => {
          const textMatch = matchesTextFilter(node);
          const typeMatch = matchesTypeFilter(node);

          // For files, both filters must match
          if (node.type === 'file') {
            if (textMatch && typeMatch) return node;
            return null;
          }

          // For folders, check children first
          if (node.children) {
            const kids = filterNodes(node.children);
            if (kids.length > 0) return { ...node, children: kids };
            // If folder name matches text filter and no type filter, include it
            if (textMatch && !hasTypeFilter) return node;
          }

          return null;
        })
        .filter(Boolean) as TreeNode[];
    };

    return filterNodes(tree);
  }, [tree, debouncedTreeFilter, fileTypeFilter, getFileExtension]);

  const fileList = useMemo(
    () => Object.values(treeIndex.byId).filter((node) => node.type === 'file'),
    [treeIndex]
  );

  // Flattened list for range selection (includes all nodes in display order)
  const flattenedFileList = useMemo(() => {
    const list: TreeNode[] = [];
    const walk = (nodes: TreeNode[]) => {
      nodes.forEach((node) => {
        list.push(node);
        const isCollapsed = node.type === 'folder' ? !!collapsed[node.id] : false;
        if (node.children && !isCollapsed) walk(node.children);
      });
    };
    walk(tree);
    return list;
  }, [tree, collapsed]);

  const applyTreeUpdate = useCallback((nextTree: TreeNode[]) => {
    setTree(nextTree);
    setTreeIndex(buildTreeIndex(nextTree));
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu({ x: 0, y: 0, node: null, open: false });
  }, []);

  const handleContextMenu = useCallback((event: React.MouseEvent, node: TreeNode) => {
    event.preventDefault();
    setSelectedNodeId(node.id);
    setContextMenu({ x: event.clientX, y: event.clientY, node, open: true });
  }, []);

  const handleSelectNode = useCallback((nodeId: string, event?: React.MouseEvent) => {
    const isMetaKey = event?.metaKey || event?.ctrlKey;
    const isShiftKey = event?.shiftKey;

    if (isMetaKey) {
      // Toggle selection (Cmd+Click)
      setMultiSelectedIds(prev => {
        const next = new Set(Array.from(prev));
        if (next.has(nodeId)) {
          next.delete(nodeId);
        } else {
          next.add(nodeId);
        }
        return next;
      });
      setLastSelectedId(nodeId);
    } else if (isShiftKey && lastSelectedId) {
      // Range selection (Shift+Click)
      const startIndex = flattenedFileList.findIndex(n => n.id === lastSelectedId);
      const endIndex = flattenedFileList.findIndex(n => n.id === nodeId);
      if (startIndex !== -1 && endIndex !== -1) {
        const [from, to] = startIndex < endIndex ? [startIndex, endIndex] : [endIndex, startIndex];
        const rangeIds = flattenedFileList.slice(from, to + 1).map(n => n.id);
        setMultiSelectedIds(new Set(rangeIds));
      }
    } else {
      // Normal click - clear multi-selection
      setMultiSelectedIds(new Set());
      setLastSelectedId(nodeId);
    }

    setSelectedNodeId(nodeId);
  }, [lastSelectedId, flattenedFileList]);

  const handleToggleCollapse = useCallback((path: string) => {
    setCollapsed((prev) => ({ ...prev, [path]: !prev[path] }));
  }, []);

  const handleRename = useCallback(() => {
    if (!contextMenu.node) return;
    const newName = window.prompt('Yeni isim', contextMenu.node.name);
    if (!newName) return closeContextMenu();
    const updated = renameNode(tree, contextMenu.node.id, newName);
    applyTreeUpdate(updated);
    closeContextMenu();
  }, [applyTreeUpdate, closeContextMenu, contextMenu.node, tree]);

  const handleDelete = useCallback(() => {
    if (!contextMenu.node) return;
    const confirmed = window.confirm(`"${contextMenu.node.name}" silinsin mi?`);
    if (!confirmed) return closeContextMenu();
    const res = removeNode(tree, contextMenu.node.id);
    applyTreeUpdate(res.tree);
    if (onDeleteCleanup) {
      onDeleteCleanup(res.removedIds);
    }
    if (selectedNodeId && res.removedIds.includes(selectedNodeId)) {
      setSelectedNodeId(null);
    }
    closeContextMenu();
  }, [applyTreeUpdate, closeContextMenu, contextMenu.node, onDeleteCleanup, selectedNodeId, tree]);

  const handleMove = useCallback(
    (sourceId: string, targetFolderId: string) => {
      const result = moveNode(tree, sourceId, targetFolderId);
      if (Object.keys(result.pathChanges).length === 0) {
        // No move happened (invalid operation)
        return;
      }
      applyTreeUpdate(result.tree);
      if (onMoveCleanup) {
        onMoveCleanup(result.pathChanges);
      }
      // Update selected node ID if it was moved
      if (selectedNodeId && result.pathChanges[selectedNodeId]) {
        setSelectedNodeId(result.pathChanges[selectedNodeId]);
      }
    },
    [applyTreeUpdate, onMoveCleanup, selectedNodeId, tree]
  );

  const resolvePath = useCallback(
    (id: string | null) => {
      if (!id) return '';
      return treeIndex.byId[id]?.path || id;
    },
    [treeIndex]
  );

  // Copy selected files to clipboard
  const handleCopy = useCallback(() => {
    const ids = multiSelectedIds.size > 0
      ? Array.from(multiSelectedIds)
      : selectedNodeId ? [selectedNodeId] : [];
    if (ids.length > 0) {
      setClipboard({ ids, operation: 'copy' });
    }
  }, [multiSelectedIds, selectedNodeId]);

  // Cut selected files to clipboard
  const handleCut = useCallback(() => {
    const ids = multiSelectedIds.size > 0
      ? Array.from(multiSelectedIds)
      : selectedNodeId ? [selectedNodeId] : [];
    if (ids.length > 0) {
      setClipboard({ ids, operation: 'cut' });
    }
  }, [multiSelectedIds, selectedNodeId]);

  // Deep clone a node with new paths
  const cloneNode = useCallback((node: TreeNode, newBasePath: string): TreeNode => {
    const newPath = newBasePath;
    const newId = newPath;
    return {
      ...node,
      id: newId,
      path: newPath,
      children: node.children?.map((child, idx) =>
        cloneNode(child, `${newPath}/${child.name}`)
      ),
    };
  }, []);

  // Generate unique name for paste/duplicate
  const generateUniqueName = useCallback((baseName: string, parentPath: string | null): string => {
    const siblings = parentPath
      ? treeIndex.byPath[parentPath]?.children || []
      : tree;
    const siblingNames = new Set(siblings.map(s => s.name));

    if (!siblingNames.has(baseName)) return baseName;

    // Extract name and extension
    const lastDot = baseName.lastIndexOf('.');
    const name = lastDot > 0 ? baseName.slice(0, lastDot) : baseName;
    const ext = lastDot > 0 ? baseName.slice(lastDot) : '';

    let counter = 1;
    let newName = `${name} copy${ext}`;
    while (siblingNames.has(newName)) {
      counter++;
      newName = `${name} copy ${counter}${ext}`;
    }
    return newName;
  }, [tree, treeIndex]);

  // Paste files from clipboard
  const handlePaste = useCallback((
    onFileCopy?: (sourcePath: string, destPath: string, content: string) => void
  ) => {
    if (!clipboard || clipboard.ids.length === 0) return;

    // Determine target folder
    let targetFolderId: string | null = null;
    if (selectedNodeId) {
      const selectedNode = treeIndex.byId[selectedNodeId];
      if (selectedNode?.type === 'folder') {
        targetFolderId = selectedNodeId;
      } else if (selectedNode) {
        // Find parent folder
        targetFolderId = findParentId(tree, selectedNodeId);
      }
    }

    const targetFolder = targetFolderId ? treeIndex.byId[targetFolderId] : null;
    const targetPath = targetFolder?.path || '';

    let updatedTree = tree;
    const pathChanges: Record<string, string> = {};

    for (const sourceId of clipboard.ids) {
      const sourceNode = findNodeById(tree, sourceId);
      if (!sourceNode) continue;

      // Generate unique name
      const newName = generateUniqueName(sourceNode.name, targetFolderId);
      const newPath = targetPath ? `${targetPath}/${newName}` : newName;

      // Clone the node with new paths
      const clonedNode = cloneNode({ ...sourceNode, name: newName }, newPath);

      // Add to tree
      if (targetFolderId) {
        updatedTree = addChild(updatedTree, targetFolderId, clonedNode);
      } else {
        updatedTree = [...updatedTree, clonedNode];
      }

      // Track path changes for file content copy
      pathChanges[sourceNode.path] = newPath;
      if (sourceNode.children) {
        const collectChildPaths = (node: TreeNode, oldBase: string, newBase: string) => {
          if (node.children) {
            node.children.forEach(child => {
              const oldPath = `${oldBase}/${child.name}`;
              const newChildPath = `${newBase}/${child.name}`;
              pathChanges[oldPath] = newChildPath;
              collectChildPaths(child, oldPath, newChildPath);
            });
          }
        };
        collectChildPaths(sourceNode, sourceNode.path, newPath);
      }
    }

    // If cut operation, remove original files
    if (clipboard.operation === 'cut') {
      for (const sourceId of clipboard.ids) {
        const result = removeNode(updatedTree, sourceId);
        updatedTree = result.tree;
      }
      setClipboard(null);
    }

    applyTreeUpdate(updatedTree);

    // Notify about file copies so content can be duplicated
    if (onFileCopy) {
      Object.entries(pathChanges).forEach(([sourcePath, destPath]) => {
        onFileCopy(sourcePath, destPath, '');
      });
    }

    return pathChanges;
  }, [clipboard, selectedNodeId, tree, treeIndex, generateUniqueName, cloneNode, applyTreeUpdate]);

  // Duplicate selected files
  const handleDuplicate = useCallback((
    onFileCopy?: (sourcePath: string, destPath: string, content: string) => void
  ) => {
    const ids = multiSelectedIds.size > 0
      ? Array.from(multiSelectedIds)
      : selectedNodeId ? [selectedNodeId] : [];

    if (ids.length === 0) return;

    let updatedTree = tree;
    const pathChanges: Record<string, string> = {};

    for (const sourceId of ids) {
      const sourceNode = findNodeById(tree, sourceId);
      if (!sourceNode) continue;

      // Find parent
      const parentId = findParentId(tree, sourceId);
      const parentPath = parentId ? treeIndex.byId[parentId]?.path : null;

      // Generate unique name
      const newName = generateUniqueName(sourceNode.name, parentId);
      const newPath = parentPath ? `${parentPath}/${newName}` : newName;

      // Clone the node
      const clonedNode = cloneNode({ ...sourceNode, name: newName }, newPath);

      // Add to tree at same level
      if (parentId) {
        updatedTree = addChild(updatedTree, parentId, clonedNode);
      } else {
        updatedTree = [...updatedTree, clonedNode];
      }

      pathChanges[sourceNode.path] = newPath;
    }

    applyTreeUpdate(updatedTree);

    // Notify about file copies
    if (onFileCopy) {
      Object.entries(pathChanges).forEach(([sourcePath, destPath]) => {
        onFileCopy(sourcePath, destPath, '');
      });
    }

    return pathChanges;
  }, [multiSelectedIds, selectedNodeId, tree, treeIndex, generateUniqueName, cloneNode, applyTreeUpdate]);

  return {
    // State
    tree,
    setTree,
    treeIndex,
    setTreeIndex,
    collapsed,
    setCollapsed,
    treeFilter,
    setTreeFilter,
    debouncedTreeFilter,
    filteredTree,
    fileList,
    selectedNodeId,
    setSelectedNodeId,
    multiSelectedIds,
    setMultiSelectedIds,
    contextMenu,
    setContextMenu,
    fileTypeFilter,
    setFileTypeFilter,
    availableFileTypes,

    // Functions
    applyTreeUpdate,
    closeContextMenu,
    handleContextMenu,
    handleSelectNode,
    handleToggleCollapse,
    handleRename,
    handleDelete,
    handleMove,
    handleCopy,
    handleCut,
    handlePaste,
    handleDuplicate,
    clipboard,
    resolvePath,
  };
}
